import { Link } from 'react-router-dom';
import { ExternalLink } from './icons';
import type { ReactNode } from 'react';
import logoUrl from '../assets/logo.webp';

export function Footer() {
  return (
    // 无界浅色 footer：底色与页面一致(bg-canvas)，顶部不加分隔线，靠留白与正文自然过渡；
    // 颜色全走语义 token，随 data-theme 自动明暗翻转（不再是硬编码 slate 深色条）。
    // 2026-08-16：三组链接由「纵向列」改为「每组一行横排」，版权/地址/备案从底部独立居中条
    // 上移到链接块正下方并左对齐（原独立条的 safe-area 底边距移到外层容器上）。
    <footer className="bg-canvas text-fg-muted">
      <div className="mx-auto w-full max-w-[1600px] px-5 pt-10 pb-[calc(2.5rem+env(safe-area-inset-bottom))] sm:px-6 sm:pt-12 sm:pb-[calc(3rem+env(safe-area-inset-bottom))]">
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:gap-x-12">
          {/* 左：三行横排链接 + 版权信息 */}
          <div className="min-w-0 flex-1">
            {/* 行距压到底：桌面三行零 gap，纯靠行高分隔（窄屏标题/链接是堆叠的，留 8px） */}
            <div className="flex flex-col gap-2 sm:gap-0">
              <FooterRow title="产品">
                <FooterLink to="/datasets">数据集浏览</FooterLink>
                {/* 会员套餐入口暂时下线（/pricing 路由保留，仅从 footer 隐藏） */}
                <FooterLink to="/redeem">兑换码</FooterLink>
                <FooterLink to="/profile">个人中心</FooterLink>
              </FooterRow>

              <FooterRow title="资源">
                <FooterLink to="/data-sources">数据来源说明</FooterLink>
                <FooterLink to="/terms">使用条款</FooterLink>
                <FooterLink to="/privacy">隐私政策</FooterLink>
              </FooterRow>

              <FooterRow title="数据库导航">
                <FooterLink to="/datasets?cat=company&lv=category">上市公司数据</FooterLink>
                <FooterLink to="/datasets?cat=province&lv=category">省级宏观数据</FooterLink>
                <FooterLink to="/datasets?cat=city&lv=category">地级市数据</FooterLink>
              </FooterRow>
            </div>

            {/* 版权 / 地址 / 备案：紧跟链接下方，与链接左缘对齐（不再居中、不再单独占底栏） */}
            <div className="mt-5 flex flex-col items-start gap-0 text-xs leading-5 text-fg-subtle">
              <p>Copyright ©2026 上海知澜方舟数据科技有限公司 版权所有</p>
              <p>地址：上海市松江区佘山镇陶干路701号5幢</p>
              <a
                href="https://beian.miit.gov.cn"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-fg"
              >
                沪ICP备2024088194号-1
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>

          {/* 右：关于我们 / 特别鸣谢 */}
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:flex-wrap sm:items-start sm:justify-center sm:gap-x-12 md:ml-auto md:justify-start md:text-left">
            {/* 移动端(堆叠)：特别鸣谢在上、关于我们在下；sm+ 行布局还原为 关于我们左 / 特别鸣谢右 */}
            <div className="order-2 sm:order-1">
              <h3 className="mb-4 text-xs font-medium tracking-widest text-fg-subtle">
                关于我们
              </h3>
              {/* 去掉旧的 brightness(0) invert(1) 白抜滤镜；改用 .header-logo：
                  亮色原色藏青、暗色自动提亮（与顶栏 logo 一致）。 */}
              <img
                src={logoUrl}
                alt="知行数据分析"
                className="header-logo mx-auto h-14 w-auto md:mx-0"
              />
            </div>

            <div className="order-1 sm:order-2">
              <h3 className="mb-4 text-xs font-medium tracking-widest text-fg-subtle">
                特别鸣谢
              </h3>
              {/* .endorsement-logo 统一处理：亮色灰度(hover 恢复原色)、暗色反相提亮；
                  opacity/hover 由该类接管，故 <a> 不再叠自己的 opacity。 */}
              <a
                href="https://cemer.com.cn"
                target="_blank"
                rel="noopener noreferrer"
                title="CEMER · 中实嘉数研究数据库"
                className="inline-block"
              >
                <img
                  src="/cemer-fulllogo.svg"
                  alt="CEMER · 中实嘉数研究数据库"
                  className="endorsement-logo h-12 w-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * 一组链接占一行：左侧固定宽的分组标题 + 右侧横排链接。
 * 窄屏（<sm）标题回到链接上方一行，避免固定宽标题把链接挤成换行梯田。
 */
function FooterRow({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-x-6 gap-y-1 sm:flex-row sm:items-baseline">
      <h3 className="shrink-0 text-xs font-medium leading-6 tracking-widest text-fg-subtle sm:w-24">
        {title}
      </h3>
      <ul className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm leading-6">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <li>
      <Link to={to} className="text-fg-muted hover:text-fg">
        {children}
      </Link>
    </li>
  );
}
